import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, RefreshCw, CheckCircle2, XCircle, MessageSquareWarning, Sparkles, ChevronDown, ChevronRight, Save, Palette, Link2, Eye, ExternalLink, AlertCircle, Loader2, Inbox, Flag, Star, MessageCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const STATUS_STYLES = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rejected: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  info_requested: "bg-sky-500/15 text-sky-300 border-sky-500/30",
};

const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  info_requested: "Needs info",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function FranchiseAdminPanel({ token }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, info_requested: 0, total: 0 });
  const [metrics, setMetrics] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [noteDrafts, setNoteDrafts] = useState({});
  const [busy, setBusy] = useState({});

  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const [appsRes, tenantsRes, metricsRes] = await Promise.all([
        axios.get(`${API}/admin/franchise/applications`, { headers: authHeaders, params }),
        axios.get(`${API}/admin/tenants`, { headers: authHeaders }),
        axios.get(`${API}/admin/tenants/metrics`, { headers: authHeaders }).catch(() => ({ data: { metrics: [] } })),
      ]);
      setRows(appsRes.data?.applications || []);
      setCounts(appsRes.data?.counts || counts);
      setTenants(tenantsRes.data?.tenants || []);
      setMetrics(metricsRes.data?.metrics || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to load franchise applications";
      toast.error(typeof msg === "string" ? msg : "Failed to load");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filter, authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const patchApp = async (id, status) => {
    setBusy((prev) => ({ ...prev, [id]: true }));
    try {
      await axios.patch(
        `${API}/admin/franchise/applications/${id}`,
        { status, admin_note: noteDrafts[id] ?? undefined },
        { headers: authHeaders },
      );
      toast.success(`Marked ${STATUS_LABEL[status] || status}`);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Update failed";
      toast.error(typeof msg === "string" ? msg : "Update failed");
    } finally {
      setBusy((prev) => ({ ...prev, [id]: false }));
    }
  };

  const provisionTenant = async (id) => {
    setBusy((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await axios.post(
        `${API}/admin/franchise/applications/${id}/create-tenant`,
        {},
        { headers: authHeaders },
      );
      const slug = res.data?.tenant?.slug;
      toast.success(res.data?.already_existed
        ? `Tenant ${slug} already provisioned`
        : `Tenant ${slug} provisioned`);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Provisioning failed";
      toast.error(typeof msg === "string" ? msg : "Provisioning failed");
    } finally {
      setBusy((prev) => ({ ...prev, [id]: false }));
    }
  };

  const tenantByAppId = useMemo(() => {
    const m = {};
    for (const t of tenants) if (t.application_id) m[t.application_id] = t;
    return m;
  }, [tenants]);

  const filterChip = (key, label, count) => (
    <button
      key={key}
      onClick={() => setFilter(key)}
      className={`px-3 py-1 rounded-full text-xs border transition-colors ${
        filter === key
          ? "bg-white text-black border-white"
          : "bg-transparent text-zinc-400 border-white/10 hover:text-white hover:border-white/30"
      }`}
      data-testid={`franchise-filter-${key}`}
    >
      {label} {typeof count === "number" && <span className="opacity-60">({count})</span>}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="franchise-admin-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#4D88FF]" />
          <h3 className="text-lg font-semibold">Franchise applications</h3>
        </div>
        <div className="flex items-center gap-2">
          <WeeklyDigestButton authHeaders={authHeaders} />
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="h-8"
            data-testid="franchise-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {filterChip("pending", "Pending", counts.pending)}
        {filterChip("info_requested", "Needs info", counts.info_requested)}
        {filterChip("approved", "Approved", counts.approved)}
        {filterChip("rejected", "Rejected", counts.rejected)}
        {filterChip("all", "All", counts.total)}
      </div>

      {tenants.length > 0 && (
        <TenantEditorList
          tenants={tenants}
          metrics={metrics}
          authHeaders={authHeaders}
          onSaved={load}
        />
      )}

      {loading ? (
        <div className="text-center text-zinc-500 py-8" data-testid="franchise-loading">Loading applications…</div>
      ) : rows.length === 0 ? (
        <div className="text-center text-zinc-500 py-8" data-testid="franchise-empty">
          No applications in this filter.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((app) => {
            const tenant = tenantByAppId[app.id];
            const isBusy = !!busy[app.id];
            return (
              <Card
                key={app.id}
                className="bg-[#121212] border-white/5"
                data-testid={`franchise-app-${app.id}`}
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-base font-semibold text-white truncate">
                        {app.business_name}
                      </p>
                      <p className="text-xs text-zinc-400">
                        {app.contact_name} · {app.city}, {app.state}
                      </p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">Submitted {fmtDate(app.created_at)}</p>
                    </div>
                    <Badge
                      className={`shrink-0 ${STATUS_STYLES[app.status] || STATUS_STYLES.pending}`}
                      data-testid={`franchise-app-status-${app.id}`}
                    >
                      {STATUS_LABEL[app.status] || app.status}
                    </Badge>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-zinc-500">Email:</span>{" "}
                      <a href={`mailto:${app.email}`} className="text-[#7AADFF] hover:underline">
                        {app.email}
                      </a>
                    </div>
                    <div>
                      <span className="text-zinc-500">Phone:</span>{" "}
                      <a href={`tel:${app.phone}`} className="text-[#7AADFF] hover:underline">
                        {app.phone}
                      </a>
                    </div>
                    {app.estimated_fleet_size ? (
                      <div>
                        <span className="text-zinc-500">Fleet:</span> {app.estimated_fleet_size}
                      </div>
                    ) : null}
                    {app.website ? (
                      <div>
                        <span className="text-zinc-500">Site:</span>{" "}
                        <a
                          href={app.website.startsWith("http") ? app.website : `https://${app.website}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#7AADFF] hover:underline"
                        >
                          {app.website}
                        </a>
                      </div>
                    ) : null}
                    {app.referral_source ? (
                      <div>
                        <span className="text-zinc-500">Referral:</span> {app.referral_source}
                      </div>
                    ) : null}
                  </div>

                  {app.note ? (
                    <div className="rounded-md bg-black/30 border border-white/5 p-3 text-xs text-zinc-300 whitespace-pre-wrap">
                      {app.note}
                    </div>
                  ) : null}

                  {app.admin_note ? (
                    <div className="rounded-md bg-[#0047FF]/5 border border-[#0047FF]/20 p-3 text-xs text-white/80">
                      <span className="text-zinc-500 text-[10px] uppercase mr-2">Admin note</span>
                      {app.admin_note}
                    </div>
                  ) : null}

                  <ApplicationPaperwork app={app} authHeaders={authHeaders} />

                  <Textarea
                    placeholder="Optional note to attach (only you see this)…"
                    value={noteDrafts[app.id] ?? ""}
                    onChange={(e) =>
                      setNoteDrafts((prev) => ({ ...prev, [app.id]: e.target.value }))
                    }
                    rows={2}
                    className="bg-black border-white/10 text-xs"
                    data-testid={`franchise-note-input-${app.id}`}
                  />

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      disabled={isBusy || app.status === "approved"}
                      onClick={() => patchApp(app.id, "approved")}
                      className="bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 border border-emerald-500/30 h-8"
                      data-testid={`franchise-approve-${app.id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      disabled={isBusy || app.status === "info_requested"}
                      onClick={() => patchApp(app.id, "info_requested")}
                      className="bg-sky-500/20 text-sky-200 hover:bg-sky-500/30 border border-sky-500/30 h-8"
                      data-testid={`franchise-request-info-${app.id}`}
                    >
                      <MessageSquareWarning className="w-3.5 h-3.5 mr-1" /> Request info
                    </Button>
                    <Button
                      size="sm"
                      disabled={isBusy || app.status === "rejected"}
                      onClick={() => patchApp(app.id, "rejected")}
                      className="bg-rose-500/20 text-rose-200 hover:bg-rose-500/30 border border-rose-500/30 h-8"
                      data-testid={`franchise-reject-${app.id}`}
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                    {app.status === "approved" && !tenant && (
                      <Button
                        size="sm"
                        disabled={isBusy}
                        onClick={() => provisionTenant(app.id)}
                        className="bg-[#0047FF] hover:bg-[#0047FF]/90 h-8 ml-auto"
                        data-testid={`franchise-provision-${app.id}`}
                      >
                        <Sparkles className="w-3.5 h-3.5 mr-1" /> Provision tenant
                      </Button>
                    )}
                    {tenant && (
                      <div className="ml-auto text-xs text-emerald-300" data-testid={`franchise-tenant-badge-${app.id}`}>
                        Tenant /{tenant.slug} · {tenant.status}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ----- Tenant editor (branding + Stripe Connect) -----

function TenantEditorList({ tenants, metrics, authHeaders, onSaved }) {
  const metricsBySlug = useMemo(() => {
    const m = {};
    (metrics || []).forEach((row) => { m[row.slug] = row; });
    return m;
  }, [metrics]);
  return (
    <Card className="bg-[#0e1730] border-[#0047FF]/30" data-testid="franchise-tenants-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[#7AADFF]" />
          <p className="text-sm font-semibold text-white">Provisioned tenants ({tenants.length})</p>
        </div>
        <div className="space-y-3">
          {tenants.map((t) => (
            <TenantEditor
              key={t.id}
              tenant={t}
              metrics={metricsBySlug[t.slug]}
              authHeaders={authHeaders}
              onSaved={onSaved}
            />
          ))}
        </div>
        <p className="text-[11px] text-zinc-500 mt-3">
          Each tenant can override their logo, wording, primary color, support phone, and per-city tax rate.
          Franchisees receive direct Stripe payouts once their Connect account ID + platform fee are configured below.
        </p>
      </CardContent>
    </Card>
  );
}

function TenantEditor({ tenant, metrics, authHeaders, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    display_name: tenant.display_name || "",
    status: tenant.status || "provisioned",
    stripe_connect_account_id: tenant.stripe_connect_account_id || "",
    platform_fee_pct: tenant.platform_fee_pct ?? "",
    logo_url: tenant.branding?.logo_url || "",
    primary_color: tenant.branding?.primary_color || "#0047FF",
    tagline: tenant.branding?.tagline || "",
    support_phone: tenant.branding?.support_phone || "",
    tax_rate_pct: tenant.branding?.tax_rate_pct ?? "",
  }));

  const update = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        display_name: form.display_name.trim() || undefined,
        status: form.status,
        stripe_connect_account_id: form.stripe_connect_account_id.trim(),
        platform_fee_pct:
          form.platform_fee_pct === "" ? undefined : Number.parseFloat(form.platform_fee_pct),
        branding: {
          logo_url: form.logo_url.trim() || null,
          primary_color: form.primary_color || null,
          tagline: form.tagline.trim() || null,
          support_phone: form.support_phone.trim() || null,
          tax_rate_pct:
            form.tax_rate_pct === "" ? null : Number.parseFloat(form.tax_rate_pct),
        },
      };
      await axios.patch(`${API}/admin/tenants/${tenant.id}`, payload, { headers: authHeaders });
      toast.success(`${tenant.display_name} updated`);
      onSaved && onSaved();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = `/?tenant=${tenant.slug}&dev=1`;

  return (
    <div
      className="rounded-xl border border-white/10 bg-black/30"
      data-testid={`franchise-tenant-${tenant.slug}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/[0.02] rounded-t-xl"
        data-testid={`tenant-editor-toggle-${tenant.slug}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {/* Health-score dot — instantly-readable "is this tenant humming?"
              summary rolling all metric chips into one traffic-light. */}
          {metrics?.health_state && (
            <div
              className="flex-shrink-0 flex flex-col items-center gap-0.5"
              title={`Health score ${metrics.health_score}/100`}
              data-testid={`health-dot-${tenant.slug}`}
            >
              <span
                className={`w-3 h-3 rounded-full ${
                  metrics.health_state === "green"
                    ? "bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.6)]"
                    : metrics.health_state === "amber"
                    ? "bg-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.6)]"
                    : "bg-rose-400 shadow-[0_0_8px_rgba(244,63,94,0.6)]"
                }`}
              />
              <span className="text-[9px] font-mono text-zinc-500">
                {metrics.health_score}
              </span>
            </div>
          )}
          <div
            className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
            style={{ background: (tenant.branding?.primary_color || "#0047FF") + "33", color: tenant.branding?.primary_color || "#0047FF" }}
          >
            {tenant.branding?.logo_url ? (
              <img src={tenant.branding.logo_url} alt="" className="w-full h-full object-contain rounded-md" onError={(e)=>{e.currentTarget.style.display='none';}} />
            ) : (
              <Building2 className="w-4 h-4" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{tenant.display_name}</p>
            <p className="text-[11px] text-zinc-500 truncate">
              /{tenant.slug} · {tenant.city}, {tenant.state} ·{" "}
              <span
                className={
                  tenant.status === "active"
                    ? "text-emerald-300"
                    : tenant.status === "suspended"
                    ? "text-rose-300"
                    : "text-amber-300"
                }
              >
                {tenant.status}
              </span>
              {tenant.stripe_connect_account_id ? (
                <> · <span className="text-[#7AADFF]">payouts on</span></>
              ) : null}
            </p>
            {/* Live metrics chips — franchise ops health at a glance */}
            {metrics && (
              <div className="flex items-center gap-1.5 flex-wrap mt-1.5" data-testid={`tenant-metrics-${tenant.slug}`}>
                <MetricChip label="Today" value={metrics.bookings_today} tone="blue" testid={`metric-today-${tenant.slug}`} />
                <MetricChip label="7d" value={metrics.bookings_week} tone="zinc" testid={`metric-week-${tenant.slug}`} />
                <MetricChip
                  label="7d $"
                  value={`$${((metrics.revenue_week_cents || 0) / 100).toFixed(0)}`}
                  tone="emerald"
                  testid={`metric-revenue-${tenant.slug}`}
                />
                <MetricChip label="Pending" value={metrics.pending_bookings} tone="amber" testid={`metric-pending-${tenant.slug}`} />
                <MetricChip label="Drivers" value={metrics.active_drivers} tone="sky" testid={`metric-drivers-${tenant.slug}`} />
                {metrics.open_support_tickets > 0 && (
                  <MetricChip
                    label="Support"
                    value={metrics.open_support_tickets}
                    tone="rose"
                    testid={`metric-tickets-${tenant.slug}`}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-white/5 pt-4" data-testid={`tenant-editor-${tenant.slug}`}>
          {/* Onboarding checklist — surfaces exactly what's left to launch */}
          <OnboardingChecklist tenant={tenant} metrics={metrics} form={form} />

          {/* Support inbox — owner triage across all franchises */}
          <SupportInbox tenant={tenant} authHeaders={authHeaders} onResolved={onSaved} />

          {/* Branding */}
          <div>
            <p className="text-xs font-semibold text-zinc-400 flex items-center gap-1 mb-2">
              <Palette className="w-3.5 h-3.5" /> Branding
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Display name</Label>
                <Input
                  value={form.display_name}
                  onChange={update("display_name")}
                  className="bg-black border-white/10 h-9 text-sm"
                  data-testid={`tenant-input-display-name-${tenant.slug}`}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Support phone</Label>
                <Input
                  value={form.support_phone}
                  onChange={update("support_phone")}
                  placeholder="+1 555-123-4567"
                  className="bg-black border-white/10 h-9 text-sm"
                  data-testid={`tenant-input-phone-${tenant.slug}`}
                />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs">Logo URL</Label>
                <Input
                  value={form.logo_url}
                  onChange={update("logo_url")}
                  placeholder="https://..."
                  className="bg-black border-white/10 h-9 text-sm"
                  data-testid={`tenant-input-logo-${tenant.slug}`}
                />
                {form.logo_url ? (
                  <img
                    src={form.logo_url}
                    alt="preview"
                    className="mt-1 max-h-16 object-contain rounded"
                    onError={(e)=>{e.currentTarget.style.display='none';}}
                  />
                ) : null}
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs">Tagline / wording</Label>
                <Textarea
                  value={form.tagline}
                  onChange={update("tagline")}
                  rows={2}
                  placeholder="Your city, your ride."
                  className="bg-black border-white/10 text-sm"
                  data-testid={`tenant-input-tagline-${tenant.slug}`}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Primary color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.primary_color || "#0047FF"}
                    onChange={update("primary_color")}
                    className="w-10 h-9 rounded border border-white/10 bg-black cursor-pointer"
                    data-testid={`tenant-input-color-${tenant.slug}`}
                  />
                  <Input
                    value={form.primary_color}
                    onChange={update("primary_color")}
                    className="bg-black border-white/10 h-9 text-sm font-mono"
                    placeholder="#0047FF"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Sales tax rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="20"
                  value={form.tax_rate_pct}
                  onChange={update("tax_rate_pct")}
                  placeholder="e.g. 6.0"
                  className="bg-black border-white/10 h-9 text-sm"
                  data-testid={`tenant-input-tax-${tenant.slug}`}
                />
              </div>
            </div>
          </div>

          {/* Payouts wizard */}
          <div>
            <p className="text-xs font-semibold text-zinc-400 flex items-center gap-1 mb-2">
              <Sparkles className="w-3.5 h-3.5" /> Stripe Connect payouts
            </p>

            <StripeOnboardingWizard
              tenant={tenant}
              authHeaders={authHeaders}
              currentAccountId={form.stripe_connect_account_id}
              onAccountLinked={(acctId) =>
                setForm((prev) => ({ ...prev, stripe_connect_account_id: acctId }))
              }
              onSaved={onSaved}
            />

            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div className="space-y-1">
                <Label className="text-xs">Platform fee %</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={form.platform_fee_pct}
                  onChange={update("platform_fee_pct")}
                  placeholder="e.g. 15"
                  className="bg-black border-white/10 h-9 text-sm"
                  data-testid={`tenant-input-fee-${tenant.slug}`}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <select
                  value={form.status}
                  onChange={update("status")}
                  className="w-full bg-black border border-white/10 rounded-md h-9 text-sm px-2"
                  data-testid={`tenant-input-status-${tenant.slug}`}
                >
                  <option value="provisioned">Provisioned</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mt-2">
              With a Connect account + fee set, every Stripe booking on this tenant sends the fare to
              the franchisee&apos;s bank and retains the platform fee in the Link Ride account.
            </p>
          </div>

          {/* Loyalty overrides */}
          <TenantLoyaltyEditor tenant={tenant} authHeaders={authHeaders} onSaved={onSaved} />

          {/* Chat quick-reply templates */}
          <TenantChatTemplatesEditor tenant={tenant} authHeaders={authHeaders} onSaved={onSaved} />

          <div className="flex items-center gap-2 pt-2 flex-wrap">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#0047FF] hover:bg-[#0047FF]/90 h-9"
              data-testid={`tenant-save-${tenant.slug}`}
            >
              <Save className="w-3.5 h-3.5 mr-1" />
              {saving ? "Saving..." : "Save tenant"}
            </Button>
            <TenantPreviewButton tenant={tenant} />
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-white/10 text-xs text-zinc-300 hover:bg-white/[0.05]"
              data-testid={`tenant-preview-${tenant.slug}`}
            >
              <Link2 className="w-3.5 h-3.5" /> Preview branded site
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

// ----- Stripe Connect Onboarding Wizard -----

const STATE_STYLE = {
  not_started: "bg-white/5 text-zinc-400 border-white/10",
  in_progress: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  under_review: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  complete: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  error: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};
const STATE_LABEL = {
  not_started: "Not started",
  in_progress: "Onboarding in progress",
  under_review: "Under review by Stripe",
  complete: "Payouts live",
  error: "Error",
};

function StripeOnboardingWizard({ tenant, authHeaders, currentAccountId, onAccountLinked, onSaved }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/tenants/${tenant.id}/stripe/status`, { headers: authHeaders });
      setStatus(res.data);
      if (res.data?.account_id && res.data.account_id !== currentAccountId && onAccountLinked) {
        onAccountLinked(res.data.account_id);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Status check failed";
      toast.error(typeof msg === "string" ? msg : "Status check failed");
    } finally {
      setLoading(false);
    }
  }, [tenant.id, authHeaders, currentAccountId, onAccountLinked]);

  useEffect(() => {
    if (tenant.stripe_connect_account_id) {
      loadStatus();
    } else {
      setStatus({ connected: false, state: "not_started", account_id: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id]);

  const startOnboarding = async () => {
    setStarting(true);
    try {
      const res = await axios.post(
        `${API}/admin/tenants/${tenant.id}/stripe/onboarding`,
        {},
        { headers: authHeaders },
      );
      if (res.data?.onboarding_url) {
        if (res.data.account_id && onAccountLinked) {
          onAccountLinked(res.data.account_id);
        }
        try {
          await axios.patch(
            `${API}/admin/tenants/${tenant.id}`,
            { stripe_connect_account_id: res.data.account_id },
            { headers: authHeaders },
          );
          onSaved && onSaved();
        } catch { /* non-fatal */ }
        window.open(res.data.onboarding_url, "_blank", "noopener,noreferrer");
        toast.success("Opening Stripe onboarding in a new tab");
      } else {
        toast.error("No onboarding URL returned");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to start onboarding";
      toast.error(typeof msg === "string" ? msg : "Failed to start onboarding");
    } finally {
      setStarting(false);
    }
  };

  const state = status?.state || "not_started";
  const acctId = status?.account_id || tenant.stripe_connect_account_id || "";
  const startLabel = acctId ? "Continue onboarding" : "Start Stripe onboarding";

  return (
    <div
      className="rounded-lg border border-white/10 bg-black/40 p-3 space-y-3"
      data-testid={`stripe-wizard-${tenant.slug}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Badge className={`border ${STATE_STYLE[state]}`} data-testid={`stripe-state-${tenant.slug}`}>
            {STATE_LABEL[state]}
          </Badge>
          {acctId ? (
            <span className="text-[11px] font-mono text-zinc-500" data-testid={`stripe-acct-id-${tenant.slug}`}>
              {acctId}
            </span>
          ) : (
            <span className="text-[11px] text-zinc-500">No Stripe account yet</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadStatus}
            disabled={loading}
            className="h-8 border-white/10 text-xs"
            data-testid={`stripe-refresh-${tenant.slug}`}
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={startOnboarding}
            disabled={starting}
            className="h-8 bg-[#635BFF] hover:bg-[#635BFF]/90 text-xs"
            data-testid={`stripe-start-${tenant.slug}`}
          >
            {starting ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <ExternalLink className="w-3.5 h-3.5 mr-1" />
            )}
            {startLabel}
          </Button>
        </div>
      </div>

      {status?.connected && status?.state !== "complete" && (
        <div className="rounded-md bg-amber-500/5 border border-amber-500/20 p-2 text-[11px] text-amber-200 flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <div>
            {status.state === "in_progress" && <p>Franchisee needs to finish Stripe onboarding.</p>}
            {status.state === "under_review" && <p>Stripe is reviewing the account. This usually takes minutes to a day.</p>}
            {(status.currently_due?.length || 0) > 0 && (
              <p className="text-amber-300/80 mt-1">
                Still needed: <span className="font-mono">{status.currently_due.slice(0, 6).join(", ")}</span>
              </p>
            )}
            {status.disabled_reason && (
              <p className="text-rose-300 mt-1">Disabled reason: {status.disabled_reason}</p>
            )}
          </div>
        </div>
      )}

      {status?.state === "complete" && (
        <div className="rounded-md bg-emerald-500/5 border border-emerald-500/20 p-2 text-[11px] text-emerald-200 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5" />
          <span>Charges + payouts enabled. Every Stripe booking on this tenant now pays out to their bank.</span>
        </div>
      )}
    </div>
  );
}

// ----- Tenant Preview (shadow-view) -----
// Opens the franchisee's live customer site in an inline iframe so support
// requests can be diagnosed without leaving the admin panel.
function TenantPreviewButton({ tenant }) {
  const [open, setOpen] = useState(false);
  const url = `/?tenant=${tenant.slug}&dev=1`;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="h-9 border-white/10 text-xs"
        data-testid={`tenant-shadow-view-${tenant.slug}`}
      >
        <Eye className="w-3.5 h-3.5 mr-1" /> Shadow view
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-2 sm:p-6"
          onClick={() => setOpen(false)}
          data-testid={`tenant-shadow-modal-${tenant.slug}`}
        >
          <div
            className="relative bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-md sm:max-w-lg h-[85vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-white/5 bg-black">
              <div className="min-w-0">
                <p className="text-sm text-white font-medium truncate">
                  Shadow view &middot; {tenant.display_name}
                </p>
                <p className="text-[10px] text-zinc-500 font-mono truncate">/{tenant.slug}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-white px-2 py-1"
                  data-testid={`tenant-shadow-newtab-${tenant.slug}`}
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open
                </a>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="p-1 rounded hover:bg-white/10 text-zinc-400"
                  data-testid={`tenant-shadow-close-${tenant.slug}`}
                  aria-label="Close"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            </div>
            <iframe
              src={url}
              title={`${tenant.display_name} preview`}
              className="w-full h-full border-0 bg-black"
              data-testid={`tenant-shadow-iframe-${tenant.slug}`}
            />
          </div>
        </div>
      )}
    </>
  );
}


// ----- Tenant metric chip -----
const TONES = {
  blue: "bg-[#0047FF]/15 text-[#7AADFF] border-[#0047FF]/30",
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  sky: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  zinc: "bg-white/5 text-zinc-300 border-white/10",
  rose: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};
function MetricChip({ label, value, tone = "zinc", testid }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${TONES[tone]}`}
      data-testid={testid}
    >
      <span className="opacity-70">{label}</span>
      <span className="font-semibold">{value ?? 0}</span>
    </span>
  );
}

// ----- Franchisee Support Inbox -----
const FLAG_LABEL = {
  cancelled: "Cancelled",
  no_show: "No-show",
  low_rating: "Low rating",
};
const FLAG_TONE = {
  cancelled: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  no_show: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  low_rating: "bg-sky-500/15 text-sky-300 border-sky-500/30",
};

function SupportInbox({ tenant, authHeaders, onResolved }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ tickets: [], flagged_bookings: [], counts: { tickets_open: 0, flagged: 0 } });
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/tenants/${tenant.id}/support-inbox`,
        { headers: authHeaders, params: { limit: 20 } },
      );
      setData(res.data || { tickets: [], flagged_bookings: [], counts: {} });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to load inbox";
      toast.error(typeof msg === "string" ? msg : "Failed to load inbox");
    } finally {
      setLoading(false);
    }
  }, [tenant.id, authHeaders]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenant.id]);

  const resolve = async (ticketId) => {
    setBusyId(ticketId);
    try {
      await axios.patch(
        `${API}/admin/support/tickets/${ticketId}`,
        { status: "resolved" },
        { headers: authHeaders },
      );
      toast.success("Marked resolved");
      await load();
      onResolved && onResolved();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Update failed";
      toast.error(typeof msg === "string" ? msg : "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const totalOpen = (data.counts?.tickets_open || 0) + (data.counts?.flagged || 0);

  return (
    <div className="rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`support-inbox-${tenant.slug}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-zinc-400 flex items-center gap-1">
          <Inbox className="w-3.5 h-3.5" /> Support inbox
          {totalOpen > 0 && (
            <span className="ml-1 inline-flex items-center rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40 px-2 text-[10px] font-semibold">
              {totalOpen}
            </span>
          )}
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          className="h-7 px-2 text-xs text-zinc-400 hover:text-white"
          data-testid={`support-inbox-refresh-${tenant.slug}`}
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {totalOpen === 0 && (
        <p className="text-[11px] text-zinc-500 text-center py-3" data-testid={`support-inbox-empty-${tenant.slug}`}>
          All clear — no open tickets or flagged trips.
        </p>
      )}

      {data.tickets.length > 0 && (
        <div className="space-y-2 mb-3">
          {data.tickets.map((t) => (
            <TicketRow
              key={t.id}
              ticket={t}
              authHeaders={authHeaders}
              busy={busyId === t.id}
              onResolve={() => resolve(t.id)}
              onReplied={load}
            />
          ))}
        </div>
      )}

      {data.flagged_bookings.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
            <Flag className="w-3 h-3" /> Flagged trips
          </p>
          {data.flagged_bookings.map((b) => (
            <div
              key={b.id}
              className="rounded-md bg-[#0d0d0d] border border-white/5 p-2.5"
              data-testid={`flagged-booking-${b.id}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${FLAG_TONE[b._flag]}`}>
                  {FLAG_LABEL[b._flag] || b.status}
                </span>
                <span className="text-xs text-white truncate">{b.customer_name || b.customer_email || "customer"}</span>
                {b.rating != null && (
                  <span className="text-[11px] text-amber-300 inline-flex items-center gap-0.5">
                    <Star className="w-3 h-3 fill-amber-400" /> {b.rating}
                  </span>
                )}
                <span className="text-[10px] text-zinc-500 ml-auto">
                  {new Date(b.created_at).toLocaleDateString()}
                </span>
              </div>
              <p className="text-[11px] text-zinc-400 mt-1 truncate">
                {b.pickup_address} → {b.dropoff_address}
              </p>
              {b.rating_comment && (
                <p className="text-[11px] text-zinc-300 mt-1 italic line-clamp-2">&ldquo;{b.rating_comment}&rdquo;</p>
              )}
              {b.cancellation_reason && (
                <p className="text-[11px] text-zinc-300 mt-1 italic line-clamp-2">
                  Reason: {b.cancellation_reason}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ----- Ticket row with reply thread -----
function TicketRow({ ticket, authHeaders, busy, onResolve, onReplied }) {
  const [expanded, setExpanded] = useState(false);
  const [replies, setReplies] = useState(ticket.replies || []);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  const loadThread = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/support/tickets/${ticket.id}`, { headers: authHeaders });
      setReplies(res.data?.replies || []);
    } catch { /* fall back to whatever we have */ }
  }, [ticket.id, authHeaders]);

  const toggle = () => {
    if (!expanded) loadThread();
    setExpanded((v) => !v);
  };

  const sendReply = async () => {
    if (draft.trim().length < 2) return toast.error("Please write a reply");
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/support/tickets/${ticket.id}/reply`,
        { message: draft.trim() },
        { headers: authHeaders },
      );
      setDraft("");
      await loadThread();
      onReplied && onReplied();
      toast.success("Reply sent");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Reply failed";
      toast.error(typeof msg === "string" ? msg : "Reply failed");
    } finally {
      setSending(false);
    }
  };

  const totalReplies = replies.length;

  return (
    <div className="rounded-md bg-[#0d0d0d] border border-white/5" data-testid={`support-ticket-${ticket.id}`}>
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-start justify-between gap-2 p-2.5 text-left hover:bg-white/[0.02] rounded-md"
        data-testid={`support-ticket-toggle-${ticket.id}`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold text-white truncate">{ticket.subject}</p>
            {totalReplies > 0 && (
              <span className="text-[10px] font-semibold rounded-full bg-[#0047FF]/20 text-[#7AADFF] border border-[#0047FF]/30 px-1.5">
                {totalReplies} reply
              </span>
            )}
          </div>
          <p className="text-[11px] text-zinc-500">
            {ticket.customer_email || ticket.customer_phone || "anonymous"} · {new Date(ticket.created_at).toLocaleString()}
          </p>
          {!expanded && (
            <p className="text-[11px] text-zinc-300 mt-1 whitespace-pre-wrap line-clamp-2">{ticket.message}</p>
          )}
        </div>
        <div className="flex-shrink-0 flex items-center gap-1">
          <Button
            size="sm"
            onClick={(e) => { e.stopPropagation(); onResolve(); }}
            disabled={busy}
            className="h-6 px-2 text-[10px] bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 border border-emerald-500/40"
            data-testid={`support-ticket-resolve-${ticket.id}`}
          >
            <CheckCircle2 className="w-3 h-3 mr-1" /> Resolve
          </Button>
          {expanded ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-white/5 p-2.5 space-y-2" data-testid={`ticket-thread-${ticket.id}`}>
          {/* Original message */}
          <div className="rounded-md bg-white/[0.03] border border-white/5 p-2">
            <p className="text-[10px] text-zinc-500 mb-0.5">
              {ticket.customer_email || "customer"} · {new Date(ticket.created_at).toLocaleString()}
            </p>
            <p className="text-[11px] text-zinc-200 whitespace-pre-wrap">{ticket.message}</p>
          </div>
          {/* Reply thread */}
          {replies.map((r) => (
            <div
              key={r.id}
              className={`rounded-md p-2 border ${
                r.author === "admin"
                  ? "bg-[#0047FF]/10 border-[#0047FF]/30 ml-6"
                  : "bg-white/[0.03] border-white/5 mr-6"
              }`}
              data-testid={`ticket-reply-${r.id}`}
            >
              <p className="text-[10px] text-zinc-500 mb-0.5">
                {r.author === "admin" ? "You" : r.author_name} · {new Date(r.created_at).toLocaleString()}
              </p>
              <p className="text-[11px] text-zinc-200 whitespace-pre-wrap">{r.message}</p>
            </div>
          ))}
          {/* Compose */}
          <div className="pt-2 space-y-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              placeholder="Type your reply — sends an email to the customer."
              className="bg-black border-white/10 text-xs"
              data-testid={`ticket-reply-draft-${ticket.id}`}
            />
            <Button
              onClick={sendReply}
              disabled={sending}
              size="sm"
              className="h-7 bg-[#0047FF] hover:bg-[#0047FF]/90 text-xs"
              data-testid={`ticket-reply-send-${ticket.id}`}
            >
              {sending ? "Sending..." : "Send reply"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


// ----- Weekly Digest button -----
function WeeklyDigestButton({ authHeaders }) {
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!window.confirm("Send the weekly digest email now?")) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/franchise/send-weekly-digest`, {}, { headers: authHeaders });
      if (res.data?.ok) {
        toast.success(`Digest sent (${res.data.tenant_count || 0} tenants)`);
      } else {
        toast.error(res.data?.reason || "Digest not sent");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to send digest";
      toast.error(typeof msg === "string" ? msg : "Failed to send digest");
    } finally {
      setSending(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={send}
      disabled={sending}
      className="h-8 border-white/10 text-xs"
      title="Ships automatically every Monday 12:00 UTC — use this to send now."
      data-testid="franchise-send-digest"
    >
      <Inbox className={`w-3.5 h-3.5 mr-1 ${sending ? "animate-pulse" : ""}`} />
      Send digest
    </Button>
  );
}

// ----- Tenant loyalty editor -----
function TenantLoyaltyEditor({ tenant, authHeaders, onSaved }) {
  const defaults = tenant.loyalty_config || {};
  const defaultRewards = defaults.rewards || [
    { points: 100, credit_cents: 500,  label: "$5 ride credit" },
    { points: 250, credit_cents: 1500, label: "$15 ride credit" },
    { points: 500, credit_cents: 3500, label: "$35 ride credit" },
  ];
  const [rate, setRate] = useState(defaults.point_rate_per_dollar ?? 1.0);
  const [multiplier, setMultiplier] = useState(defaults.pro_multiplier ?? 1.5);
  const [rewards, setRewards] = useState(defaultRewards);
  const [saving, setSaving] = useState(false);

  const updateReward = (i, field, val) => {
    setRewards((prev) => {
      const next = prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r));
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = rewards
        .map((r) => ({
          points: parseInt(r.points, 10) || 0,
          credit_cents: parseInt(r.credit_cents, 10) || 0,
          label: (r.label || "").trim() || `$${Math.round((parseInt(r.credit_cents, 10) || 0) / 100)} ride credit`,
        }))
        .filter((r) => r.points > 0 && r.credit_cents > 0);
      await axios.patch(
        `${API}/admin/tenants/${tenant.id}`,
        {
          loyalty_config: {
            point_rate_per_dollar: Number.parseFloat(rate) || 1.0,
            pro_multiplier: Number.parseFloat(multiplier) || 1.5,
            rewards: clean,
          },
        },
        { headers: authHeaders },
      );
      toast.success("Loyalty saved");
      onSaved && onSaved();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`tenant-loyalty-${tenant.slug}`}>
      <p className="text-xs font-semibold text-zinc-400 flex items-center gap-1 mb-2">
        <Star className="w-3.5 h-3.5" /> Loyalty rewards (this tenant only)
      </p>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div className="space-y-1">
          <Label className="text-xs">Points per $1 spent</Label>
          <Input
            type="number"
            step="0.1"
            min="0"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            className="bg-black border-white/10 h-9 text-sm"
            data-testid={`loyalty-rate-${tenant.slug}`}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Link Pro multiplier</Label>
          <Input
            type="number"
            step="0.1"
            min="1"
            value={multiplier}
            onChange={(e) => setMultiplier(e.target.value)}
            className="bg-black border-white/10 h-9 text-sm"
            data-testid={`loyalty-mult-${tenant.slug}`}
          />
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500">Redemption tiers</p>
        {rewards.map((r, i) => (
          <div key={i} className="grid grid-cols-3 gap-2" data-testid={`loyalty-tier-${i}-${tenant.slug}`}>
            <Input
              type="number"
              min="1"
              value={r.points}
              onChange={(e) => updateReward(i, "points", e.target.value)}
              placeholder="Points"
              className="bg-black border-white/10 h-9 text-sm"
              data-testid={`loyalty-tier-${i}-points-${tenant.slug}`}
            />
            <Input
              type="number"
              min="1"
              value={r.credit_cents}
              onChange={(e) => updateReward(i, "credit_cents", e.target.value)}
              placeholder="Credit (cents)"
              className="bg-black border-white/10 h-9 text-sm"
              data-testid={`loyalty-tier-${i}-credit-${tenant.slug}`}
            />
            <Input
              value={r.label}
              onChange={(e) => updateReward(i, "label", e.target.value)}
              placeholder="Label"
              className="bg-black border-white/10 h-9 text-sm"
              data-testid={`loyalty-tier-${i}-label-${tenant.slug}`}
            />
          </div>
        ))}
      </div>
      <Button
        onClick={save}
        disabled={saving}
        size="sm"
        className="mt-3 h-8 bg-amber-500/20 text-amber-200 hover:bg-amber-500/30 border border-amber-500/40 text-xs"
        data-testid={`loyalty-save-${tenant.slug}`}
      >
        <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving..." : "Save loyalty"}
      </Button>
      <p className="text-[11px] text-zinc-500 mt-2">
        Only affects this tenant. Leave defaults to inherit the flagship Link Ride rates.
      </p>
    </div>
  );
}


// ----- Franchisee Onboarding Checklist -----
// Reads the current tenant + metrics + editor draft form to compute completion
// state for each launch step. No API call — everything the parent already has.
function OnboardingChecklist({ tenant, metrics, form }) {
  const steps = [
    {
      key: "branding",
      label: "Branding",
      hint: "Logo, tagline, primary color set",
      done: Boolean(
        (form?.logo_url || tenant?.branding?.logo_url) ||
        (form?.tagline || tenant?.branding?.tagline) ||
        (form?.primary_color && form.primary_color.toLowerCase() !== "#0047ff"),
      ),
    },
    {
      key: "stripe",
      label: "Stripe Connect",
      hint: "Franchisee onboarded + payouts enabled",
      done: Boolean(tenant?.stripe_connect_account_id) && tenant?.stripe_payouts_enabled !== false,
      partial: Boolean(tenant?.stripe_connect_account_id) && tenant?.stripe_payouts_enabled === false,
    },
    {
      key: "fee",
      label: "Platform fee %",
      hint: "Your cut of every fare",
      done: (Number.parseFloat(form?.platform_fee_pct) || tenant?.platform_fee_pct || 0) > 0,
    },
    {
      key: "tax",
      label: "Tax rate",
      hint: "Local sales tax %",
      done: (Number.parseFloat(form?.tax_rate_pct) || tenant?.branding?.tax_rate_pct || 0) > 0,
    },
    {
      key: "phone",
      label: "Support phone",
      hint: "Number your customers call",
      done: Boolean(form?.support_phone || tenant?.branding?.support_phone),
    },
    {
      key: "drivers",
      label: "First driver",
      hint: "At least one approved driver online",
      done: (metrics?.active_drivers || 0) > 0,
    },
    {
      key: "preview",
      label: "Preview site",
      hint: "Open the shadow view to confirm the customer flow",
      done: (metrics?.bookings_today || 0) + (metrics?.bookings_week || 0) > 0,
      partial: !((metrics?.bookings_today || 0) + (metrics?.bookings_week || 0) > 0),
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);
  const readyToLaunch = doneCount >= steps.length - 1; // preview optional

  return (
    <div
      className="rounded-lg border border-white/10 bg-black/30 p-3"
      data-testid={`onboarding-checklist-${tenant.slug}`}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-zinc-400 flex items-center gap-1">
          <CheckCircle2 className="w-3.5 h-3.5" /> Launch checklist
        </p>
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${
            readyToLaunch
              ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
              : "bg-amber-500/15 text-amber-300 border-amber-500/30"
          }`}
          data-testid={`onboarding-progress-${tenant.slug}`}
        >
          {doneCount}/{steps.length} · {pct}%
        </span>
      </div>

      {/* Progress rail */}
      <div className="h-1 bg-white/5 rounded-full overflow-hidden mb-3">
        <div
          className={`h-full transition-all duration-500 ${
            readyToLaunch
              ? "bg-gradient-to-r from-emerald-500 to-emerald-300"
              : "bg-gradient-to-r from-[#0047FF] to-[#7AADFF]"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <ol className="grid sm:grid-cols-2 gap-2">
        {steps.map((s, i) => {
          const state = s.done ? "done" : s.partial ? "partial" : "todo";
          return (
            <li
              key={s.key}
              className="flex items-start gap-2 rounded-md bg-white/[0.02] border border-white/5 px-2 py-1.5"
              data-testid={`onboarding-step-${s.key}`}
              data-state={state}
            >
              <span
                className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                  state === "done"
                    ? "bg-emerald-500 text-black"
                    : state === "partial"
                    ? "bg-amber-500/30 text-amber-300 border border-amber-500/50"
                    : "bg-white/5 text-zinc-600 border border-white/10"
                }`}
              >
                {state === "done" ? (
                  <CheckCircle2 className="w-3 h-3" />
                ) : (
                  <span className="text-[9px] font-bold">{i + 1}</span>
                )}
              </span>
              <div className="min-w-0">
                <p className={`text-[12px] font-medium ${state === "done" ? "text-white line-through decoration-white/30" : "text-white"}`}>
                  {s.label}
                </p>
                <p className="text-[10px] text-zinc-500 leading-tight">{s.hint}</p>
              </div>
            </li>
          );
        })}
      </ol>

      {readyToLaunch ? (
        <p className="text-[11px] text-emerald-300 mt-3 text-center">
          Ready to launch. Share the tenant URL with the franchisee.
        </p>
      ) : (
        <p className="text-[11px] text-zinc-500 mt-3 text-center">
          Complete the remaining steps below to finish onboarding.
        </p>
      )}
    </div>
  );
}


// ----- Tenant chat templates editor -----
const DEFAULT_TEMPLATES_HINT = [
  "On my way",
  "I'm outside",
  "Running 5 min late",
  "Running 10 min late",
  "Can't find you — please share your exact location",
  "Arrived — I'll wait right here",
  "Thanks!",
].join("\n");

const DEFAULT_CUSTOMER_TEMPLATES_HINT = [
  "Thank you!",
  "I'm on my way down",
  "Please leave at door",
  "Running a couple min late",
  "Please call me",
].join("\n");

function TenantChatTemplatesEditor({ tenant, authHeaders, onSaved }) {
  const initial = (tenant.chat_templates && tenant.chat_templates.length > 0)
    ? tenant.chat_templates.join("\n")
    : "";
  const initialCust = (tenant.customer_chat_templates && tenant.customer_chat_templates.length > 0)
    ? tenant.customer_chat_templates.join("\n")
    : "";
  const [raw, setRaw] = useState(initial);
  const [rawCust, setRawCust] = useState(initialCust);
  const [saving, setSaving] = useState(false);
  const usingDefaults = raw.trim().length === 0;
  const usingCustDefaults = rawCust.trim().length === 0;

  const save = async () => {
    setSaving(true);
    try {
      const lines = raw.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 20);
      const custLines = rawCust.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 20);
      await axios.patch(
        `${API}/admin/tenants/${tenant.id}`,
        { chat_templates: lines, customer_chat_templates: custLines },
        { headers: authHeaders },
      );
      toast.success(`Templates saved (${lines.length} driver, ${custLines.length} customer)`);
      onSaved && onSaved();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => setRaw("");
  const resetCustToDefaults = () => setRawCust("");

  return (
    <div data-testid={`tenant-chat-templates-${tenant.slug}`}>
      <p className="text-xs font-semibold text-zinc-400 flex items-center gap-1 mb-2">
        <MessageCircle className="w-3.5 h-3.5" /> Chat quick-replies
      </p>
      <p className="text-[11px] text-zinc-500 mb-2">
        One phrase per line. Two lists — one for drivers, one for customers.
        Empty ⇒ inherit Link Ride defaults.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs">Driver templates</Label>
            {!usingDefaults && (
              <button
                type="button"
                onClick={resetToDefaults}
                className="text-[10px] text-zinc-500 hover:text-white underline-offset-2 hover:underline"
                data-testid={`tenant-chat-templates-reset-${tenant.slug}`}
              >
                Reset
              </button>
            )}
          </div>
          <Textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            rows={6}
            placeholder={DEFAULT_TEMPLATES_HINT}
            className="bg-black border-white/10 text-sm font-mono"
            data-testid={`tenant-chat-templates-input-${tenant.slug}`}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs">Customer templates</Label>
            {!usingCustDefaults && (
              <button
                type="button"
                onClick={resetCustToDefaults}
                className="text-[10px] text-zinc-500 hover:text-white underline-offset-2 hover:underline"
                data-testid={`tenant-chat-templates-reset-customer-${tenant.slug}`}
              >
                Reset
              </button>
            )}
          </div>
          <Textarea
            value={rawCust}
            onChange={(e) => setRawCust(e.target.value)}
            rows={6}
            placeholder={DEFAULT_CUSTOMER_TEMPLATES_HINT}
            className="bg-black border-white/10 text-sm font-mono"
            data-testid={`tenant-chat-templates-input-customer-${tenant.slug}`}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3">
        <Button
          onClick={save}
          disabled={saving}
          size="sm"
          className="h-8 bg-sky-500/20 text-sky-200 hover:bg-sky-500/30 border border-sky-500/40 text-xs"
          data-testid={`tenant-chat-templates-save-${tenant.slug}`}
        >
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving..." : "Save templates"}
        </Button>
        {usingDefaults && usingCustDefaults && (
          <span className="text-[10px] text-zinc-500">Using Link Ride defaults</span>
        )}
      </div>
    </div>
  );
}



// ----- ApplicationPaperwork: qualifying answers + signed-agreement log -----
//
// Expandable panel rendered on every franchise application card. Fetches
// the acceptance log lazily on first expand — no unnecessary requests
// when the admin just skims the list.

const INSURANCE_LABEL = {
  active: "Active policy in place",
  pending: "Application in progress",
  none: "No commercial policy yet",
  not_applicable: "Not applicable (licensing only)",
};
const TNC_LABEL = {
  active: "Approved / active",
  pending: "Application filed",
  not_required: "Not required in this jurisdiction",
  unsure: "Unsure — needs guidance",
};

function ApplicationPaperwork({ app, authHeaders }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acceptances, setAcceptances] = useState(null);

  const q = app.qualifying || null;
  const hasQualifying = !!q && Object.keys(q).length > 0;

  const loadAcceptances = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/legal/acceptances`, {
        headers: authHeaders,
        params: { franchise_application_id: app.id, limit: 50 },
      });
      setAcceptances(res.data?.acceptances || []);
    } catch {
      setAcceptances([]);
    } finally {
      setLoading(false);
    }
  }, [app.id, authHeaders]);

  const toggle = () => {
    if (!open && acceptances === null) loadAcceptances();
    setOpen((v) => !v);
  };

  const opsBits = [];
  if (q?.operates_own_fleet) opsBits.push("Own fleet");
  if (q?.licenses_only) opsBits.push("Licenses-only sub-licensor");

  return (
    <div
      className="rounded-md bg-black/30 border border-white/5"
      data-testid={`application-paperwork-${app.id}`}
    >
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-white/[0.02] rounded-md"
        data-testid={`paperwork-toggle-${app.id}`}
      >
        <span className="text-xs font-semibold text-zinc-400 flex items-center gap-2">
          <FileText className="w-3.5 h-3.5" />
          Paperwork
          {(acceptances?.length ?? 0) > 0 && (
            <span className="inline-flex items-center rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-1.5 text-[10px] font-semibold">
              {acceptances.length} signed
            </span>
          )}
          {!hasQualifying && !acceptances?.length && (
            <span className="text-[10px] text-zinc-600 font-normal">— none captured</span>
          )}
        </span>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />}
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-white/5" data-testid={`paperwork-body-${app.id}`}>
          {/* Qualifying block */}
          {hasQualifying ? (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Compliance & operating model</p>
              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                <PaperRow label="Legal name" value={q.legal_business_name} />
                <PaperRow label="EIN / Tax ID" value={q.ein} mono />
                <PaperRow
                  label="States of operation"
                  value={(q.states_of_operation || []).join(", ") || "—"}
                />
                <PaperRow
                  label="Commercial insurance"
                  value={INSURANCE_LABEL[q.commercial_insurance_status] || "—"}
                />
                <PaperRow
                  label="TNC permit"
                  value={TNC_LABEL[q.tnc_permit_status] || "—"}
                />
                <PaperRow
                  label="Responsible party"
                  value={
                    q.responsible_party_name
                      ? `${q.responsible_party_name}${q.responsible_party_title ? ` — ${q.responsible_party_title}` : ""}`
                      : "—"
                  }
                />
                <PaperRow
                  label="Operating model"
                  value={opsBits.length ? opsBits.join(" · ") : "—"}
                />
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-zinc-600 italic">No compliance answers captured — this application predates the qualifying form.</p>
          )}

          {/* Acceptance log */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
              Signed agreements
            </p>
            {loading && <p className="text-[11px] text-zinc-500">Loading…</p>}
            {!loading && (!acceptances || acceptances.length === 0) && (
              <p className="text-[11px] text-zinc-600 italic">
                No acceptance rows on record for this application.
              </p>
            )}
            {!loading && acceptances && acceptances.length > 0 && (
              <ul className="space-y-1.5" data-testid={`paperwork-acceptances-${app.id}`}>
                {acceptances.map((row) => (
                  <li
                    key={row.id}
                    className="flex items-start justify-between gap-3 text-[11px] bg-emerald-500/5 border border-emerald-500/20 rounded px-2 py-1.5"
                    data-testid={`paperwork-accept-row-${row.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-emerald-200 font-semibold">
                        {row.doc_id}
                        <span className="ml-1 text-emerald-400/60 font-normal">v{row.doc_version}</span>
                      </p>
                      <p className="text-zinc-500 truncate">
                        {row.signer_name} · {row.signer_email}
                      </p>
                    </div>
                    <div className="text-right text-zinc-500 shrink-0">
                      <p>{new Date(row.accepted_at).toLocaleDateString()}</p>
                      {row.ip && <p className="font-mono text-[9px] text-zinc-600">{row.ip}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PaperRow({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-2 min-w-0">
      <span className="text-zinc-500 text-[10px] uppercase tracking-wider shrink-0 w-28">{label}</span>
      <span className={`text-zinc-200 truncate ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </div>
  );
}
